import { useState } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PawPrint, CheckCircle2, ShieldCheck, FileCheck2, Bone, Check } from "lucide-react";
import { Link } from "react-router-dom";

const PROGRAMS = [
  {
    name: "Puppy Foundations",
    blurb: "Crate, potty & socialization basics for pups 8–20 weeks.",
    img: "https://images.unsplash.com/photo-1760615303236-e5923d66a450?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  },
  {
    name: "Basic Obedience",
    blurb: "Sit, stay, come & leash manners for everyday control.",
    img: "https://images.unsplash.com/photo-1596278852720-141ba859bbbf?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  },
  {
    name: "Advanced Obedience",
    blurb: "Off-leash reliability and distraction-proof commands.",
    img: "https://images.pexels.com/photos/10013055/pexels-photo-10013055.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
  },
  {
    name: "Behavior Correction",
    blurb: "Fix leash reactivity, barking, jumping & anxiety.",
    img: "https://images.unsplash.com/photo-1640652663796-764e4eb5bc59?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  },
  {
    name: "Protection / Working",
    blurb: "Personal protection & working-dog skill development.",
    img: "https://images.unsplash.com/photo-1781109460079-033d0493ef31?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  },
  {
    name: "Agility & Sport",
    blurb: "Speed, jumps & weaves for active, driven dogs.",
    img: "https://images.unsplash.com/photo-1581753418434-51c11169a3c1?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  },
  {
    name: "Other",
    blurb: "Not sure yet? Pick this and we'll tailor a plan with you.",
    img: "https://images.unsplash.com/photo-1477936432016-8172ed08637e?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

const CONSENT_TEXT =
  "By checking this box and submitting this form, I agree to receive calls and SMS/text messages from Clawius regarding my dog's training enrollment, using the phone number provided, including messages sent via automated technology. Consent is not a condition of any purchase. Message and data rates may apply. Reply STOP to opt out. I have read and agree to the Terms, Privacy Policy, and SMS Terms.";

const empty = {
  first_name: "",
  last_name: "",
  zip: "",
  phone: "",
  email: "",
  device_type: "",
  brand: "",
  model: "",
};

export default function Home() {
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.device_type) {
      toast.error("Please choose a training program.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the consent notice to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/registrations", {
        ...form,
        tcpa_consent: consent,
        source_url: window.location.href,
      });
      setDone(true);
      toast.success("Your dog is enrolled.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Enrollment failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-background">
        <img
          src="https://images.pexels.com/photos/11570516/pexels-photo-11570516.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400"
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-right opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-36 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-sm border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-heading font-semibold uppercase tracking-[0.25em] text-primary">
              <PawPrint className="h-3.5 w-3.5" /> Enroll. Train. Transform.
            </span>
            <h1 className="mt-6 font-display text-6xl uppercase leading-[0.9] tracking-wide text-white sm:text-7xl lg:text-8xl">
              Build a better<br />
              <span className="text-primary">dog.</span>
            </h1>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-zinc-300 md:text-lg">
              Clawius pairs your dog with proven, results-driven training. Enroll
              in one quick form — your details and consent are captured clearly
              and stored securely.
            </p>
            <a href="#register" data-testid="hero-register-cta" className="mt-8 inline-block">
              <Button
                size="lg"
                className="rounded-sm bg-primary px-9 py-6 font-heading text-base font-bold uppercase tracking-wider text-primary-foreground transition-all hover:-translate-y-0.5 hover:bg-orange-500 hover:shadow-[0_8px_30px_rgba(255,95,31,0.35)]"
              >
                Enroll Your Dog
              </Button>
            </a>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 font-body text-sm text-zinc-300">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Private records</span>
              <span className="inline-flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-primary" /> Consent proof stored</span>
              <span className="inline-flex items-center gap-2"><Bone className="h-4 w-4 text-primary" /> One dog, one record</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registration form */}
      <section id="register" className="mx-auto max-w-4xl px-6 py-20 md:py-28 lg:px-8">
        {done ? (
          <div className="mx-auto max-w-3xl rounded-sm border border-border bg-card p-10 text-center" data-testid="registration-success">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-white">
              You're enrolled
            </h2>
            <p className="mt-3 font-body text-zinc-400">
              Your dog's training enrollment and contact details are saved along
              with your consent record. Our team will reach out with next steps.
            </p>
            <Button
              variant="outline"
              className="mt-6 rounded-sm border-border bg-transparent text-white hover:border-primary hover:bg-secondary"
              data-testid="register-another-button"
              onClick={() => {
                setForm(empty);
                setConsent(false);
                setDone(false);
              }}
            >
              Enroll another dog
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-primary">
                Dog Enrollment
              </p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Tell us about your dog
              </h2>
              <p className="mt-3 font-body text-zinc-400">
                All fields are required. We check for duplicates by phone and email.
              </p>
            </div>

            <form onSubmit={submit} className="rounded-sm border border-border bg-card p-6 md:p-8" data-testid="registration-form">
              <p className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500">Owner details</p>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <Field label="First name" id="first_name">
                  <Input data-testid="input-first-name" required value={form.first_name} onChange={set("first_name")} placeholder="Jane" />
                </Field>
                <Field label="Last name" id="last_name">
                  <Input data-testid="input-last-name" required value={form.last_name} onChange={set("last_name")} placeholder="Doe" />
                </Field>
                <Field label="ZIP code" id="zip">
                  <Input data-testid="input-zip" required value={form.zip} onChange={set("zip")} placeholder="90210" />
                </Field>
                <Field label="Phone" id="phone">
                  <Input data-testid="input-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Email" id="email">
                    <Input data-testid="input-email" required type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" />
                  </Field>
                </div>
              </div>

              <div className="my-8 h-px bg-border" />

              {/* Program picker */}
              <div className="flex items-center justify-between">
                <p className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500">Choose a training program</p>
                {form.device_type && (
                  <span className="font-heading text-xs font-semibold uppercase tracking-wide text-primary" data-testid="selected-program-label">
                    {form.device_type}
                  </span>
                )}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="program-picker">
                {PROGRAMS.map((p) => {
                  const active = form.device_type === p.name;
                  return (
                    <button
                      type="button"
                      key={p.name}
                      data-testid={`program-card-${slug(p.name)}`}
                      aria-pressed={active}
                      onClick={() => set("device_type")(p.name)}
                      className={`group relative overflow-hidden rounded-sm border text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                        active
                          ? "border-primary shadow-[0_0_0_1px_hsl(17_100%_56%)]"
                          : "border-border hover:-translate-y-0.5 hover:border-primary/60"
                      }`}
                    >
                      <div className="relative h-28 w-full overflow-hidden">
                        <img
                          src={p.img}
                          alt={p.name}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-card/90 via-card/20 to-transparent" />
                        {active && (
                          <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                            <Check className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                      <div className="bg-card p-3">
                        <p className="font-heading text-sm font-semibold uppercase tracking-wide text-white">{p.name}</p>
                        <p className="mt-1 font-body text-xs leading-relaxed text-zinc-400">{p.blurb}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Field label="Breed" id="brand">
                  <Input data-testid="input-brand" required value={form.brand} onChange={set("brand")} placeholder="German Shepherd" />
                </Field>
                <Field label="Dog's name" id="model">
                  <Input data-testid="input-model" required value={form.model} onChange={set("model")} placeholder="Rex" />
                </Field>
              </div>

              {/* TCPA consent */}
              <div className="mt-8 rounded-sm border border-primary/40 bg-primary/5 p-5">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="tcpa"
                    data-testid="tcpa-consent-checkbox"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(!!v)}
                    className="mt-1 h-5 w-5 border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                  />
                  <Label htmlFor="tcpa" className="cursor-pointer font-body text-sm leading-relaxed text-zinc-400">
                    {CONSENT_TEXT.replace(" I have read and agree to the Terms, Privacy Policy, and SMS Terms.", " ")}
                    I have read and agree to the{" "}
                    <Link to="/terms" className="font-semibold text-primary underline">Terms</Link>,{" "}
                    <Link to="/privacy" className="font-semibold text-primary underline">Privacy Policy</Link>, and{" "}
                    <Link to="/sms-terms" className="font-semibold text-primary underline">SMS Terms</Link>.
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="register-submit-button"
                disabled={submitting}
                className="mt-6 w-full rounded-sm bg-primary py-6 font-heading text-base font-bold uppercase tracking-wider text-primary-foreground transition-all hover:bg-orange-500 sm:w-auto sm:px-12"
              >
                {submitting ? "Enrolling…" : "Enroll Dog"}
              </Button>
            </form>
          </>
        )}
      </section>
    </PublicLayout>
  );
}

const Field = ({ label, id, children }) => (
  <div>
    <Label htmlFor={id} className="mb-2 block font-heading text-sm font-semibold uppercase tracking-wide text-zinc-300">
      {label}
    </Label>
    {children}
  </div>
);
